import React, { useEffect, RefObject } from 'react';
import Hls, { HlsConfig } from 'hls.js';

export interface HlsPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  hlsConfig?: Partial<HlsConfig>;
  playerRef: RefObject<HTMLVideoElement>;
  src: string;
  type: 'movie' | 'audio';
}

export function ReactHlsPlayer({
                                 hlsConfig,
                                 playerRef = React.createRef<HTMLVideoElement>(),
                                 src,
                                 autoPlay,
                                 type,
                                 ...props
                               }: HlsPlayerProps) {
  useEffect(() => {
    let hls: Hls;

    function _initPlayer() {
      if (hls != null) {
        hls.destroy();
      }

      const newHls = new Hls({
        enableWorker: false,
        ...hlsConfig,
      });

      if (playerRef.current != null) {
        newHls.attachMedia(playerRef.current);
      }

      newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
        newHls.loadSource(src);

        newHls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (autoPlay) {
            playerRef?.current
              ?.play()
              .catch(() => console.log('Unable to autoplay prior to user interaction with the dom.'));
          }
        });
      });

      newHls.on(Hls.Events.ERROR, function (event, data) {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              newHls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              newHls.recoverMediaError();
              break;
            default:
              _initPlayer();
              break;
          }
        }
      });

      hls = newHls;
    }

    // Check for Media Source support
    if (Hls.isSupported()) {
      _initPlayer();
    }

    if (playerRef.current && props.muted) {
      playerRef.current.volume = 0;
    }

    return () => {
      if (hls != null) {
        hls.destroy();
      }
    };
  }, [autoPlay, hlsConfig, playerRef, src]);

  // If Media Source is supported, use HLS.js to play video
  if (Hls.isSupported()) {
    if (type == 'movie') {
      return <video ref={playerRef} controls={true} {...props} />;
    } else {
      return <audio ref={playerRef} {...props} />;
    }
  } else {
    // Fallback to using a regular video player if HLS is supported by default in the user's browser
    if (type == 'movie') {
      if (props.muted) {
        return (
          <video muted ref={playerRef} autoPlay={autoPlay} {...props}>
            <source src={src} type="application/x-mpegURL" />
          </video>
        );
      } else {
        return (
          <video ref={playerRef} autoPlay={autoPlay} {...props}>
            <source src={src} type="application/x-mpegURL" />
          </video>
        );
      }
    } else {
      return (
        <audio ref={playerRef} autoPlay={autoPlay} {...props}>
          <source src={src} type="application/x-mpegURL" />
        </audio>
      );
    }
  }
}
